// DEM-2550 — seletores equivalentes ao Telegram (/model /effort /reasoning)
// consomem as mesmas APIs do Command Control Plane. Mudanças refletem sem restart.
// DEM-2580 — linha 📊 de quota espelhando o /status (snapshot real da conta efetiva).

import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type CanonicalEffort, type QuotaWindow } from '@/lib/api';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Select';
import { ReasoningSlider } from '@/components/llm/ReasoningSlider';
import { cn } from '@/lib/utils';

/** Formata a linha 📊 igual ao renderer Telegram do /status (sem inventar %). */
function formatQuotaLine(
  windows: QuotaWindow[] | null | undefined,
  capturedAt?: string | null,
): string | null {
  if (!windows || windows.length === 0) return null;
  const parts = windows.map((w) => {
    const pct =
      w.utilization != null
        ? `${Math.round(Math.max(0, Math.min(1, 1 - w.utilization)) * 100)}% left`
        : 'n/a';
    return `${w.label} ${pct}`;
  });
  let age = '';
  if (capturedAt) {
    const t = Date.parse(capturedAt);
    if (Number.isFinite(t)) {
      const min = Math.max(0, Math.round((Date.now() - t) / 60_000));
      age = min < 1 ? ' · agora' : min < 60 ? ` · há ${min}min` : ` · há ${Math.round(min / 60)}h`;
    }
  }
  return `📊 ${parts.join(' · ')}${age}`;
}

type Scope = 'session' | 'permanent';
type ReasoningMode = 'off' | 'on' | 'auto';

interface ControlPlanePanelProps {
  companionId: string;
  sessionId?: string | null;
  compact?: boolean;
  className?: string;
  onApplied?: () => void;
}

export function ControlPlanePanel({
  companionId,
  sessionId,
  compact = false,
  className,
  onApplied,
}: ControlPlanePanelProps) {
  const qc = useQueryClient();
  // Overrides locais do usuário (só mudam após interação). Defaults vêm do
  // estado efetivo via useMemo — evita setState síncrono em useEffect (react-hooks).
  const [providerOverride, setProviderOverride] = useState<string | null>(null);
  const [modelKeyOverride, setModelKeyOverride] = useState<string | null>(null);
  const [effortOverride, setEffortOverride] = useState<CanonicalEffort | null | undefined>(undefined);
  const [reasoningOverride, setReasoningOverride] = useState<ReasoningMode | null | undefined>(undefined);
  const [scope, setScope] = useState<Scope>('session');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');

  const stateQuery = useQuery({
    queryKey: ['control-plane-state', companionId, sessionId ?? null],
    queryFn: () =>
      api.controlPlaneState({
        companion_id: companionId,
        ...(sessionId ? { session_id: sessionId } : {}),
      }),
    enabled: !!companionId,
    staleTime: 15_000,
  });

  // DEM-2580: cota da conta efetiva (mesma fonte do card LLM + /status).
  const accountsQuery = useQuery({
    queryKey: ['llm-accounts-with-status'],
    queryFn: () => api.listLlmAccountsWithStatus(),
    staleTime: 30_000,
  });

  const effective = stateQuery.data?.state?.effective;
  const provider = providerOverride ?? effective?.provider ?? '';
  const modelKey = modelKeyOverride ?? effective?.model_key ?? '';
  const effort =
    effortOverride === undefined ? (effective?.effort ?? undefined) : (effortOverride ?? undefined);
  const reasoning =
    reasoningOverride === undefined
      ? ((effective?.reasoning as ReasoningMode | null) ?? undefined)
      : (reasoningOverride ?? undefined);

  const setProvider = (v: string) => setProviderOverride(v);
  const setModelKey = (v: string) => setModelKeyOverride(v);
  const setEffort = (v: CanonicalEffort | undefined) => setEffortOverride(v ?? null);
  const setReasoning = (v: ReasoningMode | undefined) => setReasoningOverride(v ?? null);

  const providersQuery = useQuery({
    queryKey: ['control-plane-providers'],
    queryFn: () => api.controlPlaneProviders(),
    staleTime: 60_000,
  });

  const modelsQuery = useQuery({
    queryKey: ['control-plane-models', provider],
    queryFn: () => api.controlPlaneModels(provider),
    enabled: !!provider,
    staleTime: 60_000,
  });

  const effortLevels = useMemo(() => {
    const fromState = stateQuery.data?.state?.capabilities.effort_levels ?? [];
    const fromModel = (modelsQuery.data?.models ?? []).find((m) => m.model_key === modelKey)
      ?.supported_effort_levels;
    return fromModel && fromModel.length > 0 ? fromModel : fromState;
  }, [stateQuery.data, modelsQuery.data, modelKey]);

  const applyMut = useMutation({
    mutationFn: (payload: Parameters<typeof api.controlPlaneApply>[0]) =>
      api.controlPlaneApply(payload),
    onSuccess: async (res) => {
      setError('');
      setInfo(res.message ?? 'Aplicado.');
      await qc.invalidateQueries({ queryKey: ['control-plane-state'] });
      onApplied?.();
    },
    onError: (err: unknown) => {
      setInfo('');
      setError(err instanceof Error ? err.message : 'Falha ao aplicar seleção.');
    },
  });

  const busy = applyMut.isPending;
  const degradation = stateQuery.data?.state?.degradation_reason;
  const providers = providersQuery.data?.providers ?? [];
  const models = modelsQuery.data?.models ?? [];
  const unavailableModels = modelsQuery.data?.unavailable_reason;

  function applyModel() {
    if (!provider || !modelKey) {
      setError('Escolha provider e modelo.');
      return;
    }
    if (scope === 'session' && !sessionId) {
      setError('Override de sessão exige uma sessão ativa.');
      return;
    }
    applyMut.mutate({
      companion_id: companionId,
      session_id: sessionId,
      provider,
      model_key: modelKey,
      scope,
    });
  }

  function applyEffort(next: CanonicalEffort) {
    setEffort(next);
    if (scope === 'session' && !sessionId) {
      setError('Override de sessão exige uma sessão ativa.');
      return;
    }
    applyMut.mutate({
      companion_id: companionId,
      session_id: sessionId,
      effort: next,
      scope,
    });
  }

  function applyReasoning(next: ReasoningMode) {
    setReasoning(next);
    if (scope === 'session' && !sessionId) {
      setError('Override de sessão exige uma sessão ativa.');
      return;
    }
    applyMut.mutate({
      companion_id: companionId,
      session_id: sessionId,
      reasoning: next,
      scope,
    });
  }

  const st = stateQuery.data?.state;

  // Conta efetiva → janelas de cota (só quando snapshot real existir).
  const effectiveAccountId = st?.effective?.account_id ?? null;
  const effectiveAccount = useMemo(() => {
    const list = accountsQuery.data ?? [];
    if (effectiveAccountId) {
      const byId = list.find((a) => a.id === effectiveAccountId);
      if (byId) return byId;
    }
    // Fallback: primeira conta do provider efetivo com snapshot.
    const prov = st?.effective?.provider;
    if (prov) {
      return (
        list.find((a) => a.provider === prov && a.quota_windows && a.quota_windows.length > 0) ??
        list.find((a) => a.provider === prov) ??
        null
      );
    }
    return list.find((a) => a.quota_windows && a.quota_windows.length > 0) ?? null;
  }, [accountsQuery.data, effectiveAccountId, st?.effective?.provider]);

  const quotaLine = formatQuotaLine(
    effectiveAccount?.quota_windows ?? null,
    effectiveAccount?.quota_captured_at ?? null,
  );

  const refreshQuotaMut = useMutation({
    mutationFn: () => api.listLlmAccountsWithStatus({ refresh: true }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['llm-accounts-with-status'] });
      setInfo('Cota atualizada.');
    },
    onError: (err: unknown) => {
      setError(err instanceof Error ? err.message : 'Falha ao atualizar cota.');
    },
  });

  return (
    <div
      className={cn(
        'rounded-lg border border-raios-border bg-raios-surface p-3 space-y-3',
        compact && 'p-2 space-y-2',
        className,
      )}
      data-testid="control-plane-panel"
    >
      <div className="flex items-center justify-between gap-2">
        <div>
          <div className="text-sm font-medium text-raios-text">Control Plane</div>
          <div className="text-xs text-raios-muted">
            Provider · modelo · effort · reasoning — mesmas regras do Telegram
          </div>
        </div>
        <div className="flex gap-1">
          <Button
            type="button"
            size="sm"
            variant={scope === 'session' ? 'brand' : 'outline'}
            onClick={() => setScope('session')}
            disabled={busy}
          >
            Sessão
          </Button>
          <Button
            type="button"
            size="sm"
            variant={scope === 'permanent' ? 'brand' : 'outline'}
            onClick={() => setScope('permanent')}
            disabled={busy}
          >
            Permanente
          </Button>
        </div>
      </div>

      {st && (
        <div className="text-xs text-raios-muted grid grid-cols-2 gap-x-3 gap-y-1">
          <span>
            Efetivo: <strong className="text-raios-text">{st.effective.provider ?? '—'}/
            {st.effective.model_key ?? '—'}</strong>
          </span>
          <span>
            Auth: <strong className="text-raios-text">{st.effective.auth_method ?? '—'}</strong>
          </span>
          <span>
            Conta: <strong className="text-raios-text">{st.effective.account_label ?? '—'}</strong>
          </span>
          <span>
            Effort/Reasoning:{' '}
            <strong className="text-raios-text">
              {st.effective.effort ?? '—'} / {st.effective.reasoning ?? '—'}
            </strong>
          </span>
          {st.session_override && (
            <span className="col-span-2">
              Override de sessão ativo (não altera o padrão permanente).
            </span>
          )}
        </div>
      )}

      {/* DEM-2580: mesma linha 📊 do /status — só com snapshot real. */}
      <div className="flex items-center justify-between gap-2" data-testid="control-plane-quota">
        {quotaLine ? (
          <div className="text-xs text-raios-text font-mono">{quotaLine}</div>
        ) : (
          <div className="text-xs text-raios-muted">
            📊 cota indisponível (sem snapshot real — não inventado)
          </div>
        )}
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() => refreshQuotaMut.mutate()}
          loading={refreshQuotaMut.isPending}
          disabled={refreshQuotaMut.isPending}
          title="Reusa GET /llm-accounts?refresh=1 do serviço de contas"
        >
          Atualizar cota
        </Button>
      </div>

      {degradation && (
        <div className="text-xs text-amber-600 bg-amber-50 dark:bg-amber-950/30 rounded px-2 py-1">
          Indisponível / degradação: {degradation}
        </div>
      )}

      {providers.length === 0 && !providersQuery.isLoading && (
        <div className="text-xs text-raios-muted">
          Nenhum provider com conta elegível. Conecte Anthropic, OpenAI ou Grok nas Configurações.
          Nenhuma API paga é usada automaticamente.
        </div>
      )}

      <div className={cn('grid gap-2', compact ? 'grid-cols-1' : 'grid-cols-2')}>
        <Select
          label="Provider"
          placeholder={providersQuery.isLoading ? 'Carregando…' : 'Escolha…'}
          value={provider}
          onChange={(v) => {
            setProvider(v);
            setModelKey('');
          }}
          options={providers.map((p) => ({
            value: p.key,
            label: p.display_name,
            description: p.accounts.map((a) => a.auth_method).join(', '),
          }))}
          disabled={busy || providers.length === 0}
        />
        <Select
          label="Modelo"
          placeholder={
            !provider
              ? 'Escolha o provider…'
              : modelsQuery.isLoading
                ? 'Carregando…'
                : unavailableModels
                  ? 'Indisponível'
                  : 'Escolha…'
          }
          value={modelKey}
          onChange={setModelKey}
          options={models.map((m) => ({
            value: m.model_key,
            label: m.display_name,
            description: m.tier ?? undefined,
          }))}
          disabled={busy || !provider || models.length === 0}
        />
      </div>

      {unavailableModels && (
        <div className="text-xs text-amber-600">
          Modelos indisponíveis: {unavailableModels}. {modelsQuery.data?.message}
        </div>
      )}

      <div className="flex justify-end">
        <Button type="button" size="sm" variant="brand" onClick={applyModel} loading={busy} disabled={!provider || !modelKey}>
          Aplicar modelo ({scope === 'session' ? 'sessão' : 'permanente'})
        </Button>
      </div>

      <ReasoningSlider
        label="Effort"
        value={effort}
        onChange={(lvl) => applyEffort(lvl)}
        supportedLevels={effortLevels}
      />

      <div className="space-y-1">
        <div className="text-sm font-medium text-raios-text">Reasoning (separado de effort)</div>
        <div className="flex gap-1">
          {(['off', 'on', 'auto'] as ReasoningMode[]).map((m) => {
            const supported = st?.capabilities.reasoning_supported !== false;
            return (
              <button
                key={m}
                type="button"
                disabled={busy || !supported}
                onClick={() => applyReasoning(m)}
                className={cn(
                  'flex-1 px-2 py-1.5 text-xs rounded-md border transition-colors',
                  reasoning === m
                    ? 'bg-raios-accent text-white border-raios-accent'
                    : supported
                      ? 'bg-raios-surface text-raios-text border-raios-border hover:bg-raios-surface-alt'
                      : 'opacity-40 cursor-not-allowed',
                )}
              >
                {m}
              </button>
            );
          })}
        </div>
        {st?.capabilities.reasoning_supported === false && (
          <div className="text-xs text-raios-muted">
            Reasoning indisponível para o modelo ativo (sem inventar suporte).
          </div>
        )}
      </div>

      {error && <div className="text-xs text-red-600">{error}</div>}
      {info && !error && <div className="text-xs text-emerald-600">{info}</div>}
    </div>
  );
}
